import { createRootRoute, createRoute, createLazyRoute } from "@tanstack/react-router";
import { RootLayout } from "./routes/__root";
import { StoreLayout } from "./routes/store-layout";

const rootRoute = createRootRoute({ component: RootLayout });

const directoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
}).lazy(() => import("./routes/index").then((m) => createLazyRoute("/")({ component: m.DirectoryPage })));

// The digital receipt (POS QR / table Bill QR): standalone, no store session needed.
const receiptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "r/$orderId",
}).lazy(() => import("./routes/receipt").then((m) => createLazyRoute("/r/$orderId")({ component: m.ReceiptPage })));

// The scanner outside a store (Discover's Scan button).
const scanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "scan",
}).lazy(() => import("./routes/store-scan").then((m) => createLazyRoute("/scan")({ component: m.ScanPage })));

// The guest's own tabs, outside any one store.
const cardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "cards",
}).lazy(() => import("./routes/account").then((m) => createLazyRoute("/cards")({ component: m.CardsPage })));
const meRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "me",
}).lazy(() => import("./routes/account").then((m) => createLazyRoute("/me")({ component: m.MePage })));
// Guest sign-in: one account for every store.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "signin",
}).lazy(() => import("./routes/signin").then((m) => createLazyRoute("/signin")({ component: m.SignInPage })));
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "signup",
}).lazy(() => import("./routes/signin").then((m) => createLazyRoute("/signup")({ component: m.SignUpPage })));
const protectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "protect",
}).lazy(() => import("./routes/signin").then((m) => createLazyRoute("/protect")({ component: m.ProtectPage })));
const receiptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "receipts",
}).lazy(() => import("./routes/account").then((m) => createLazyRoute("/receipts")({ component: m.ReceiptsPage })));
const offersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "offers",
}).lazy(() => import("./routes/account").then((m) => createLazyRoute("/offers")({ component: m.AllOffersPage })));

const storeLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "store/$storeId",
  component: StoreLayout,
});

const storeHomeRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "/",
}).lazy(() =>
  import("./routes/store-home").then((m) => createLazyRoute("/store/$storeId/")({ component: m.StoreHomePage })),
);

const storeScanRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "scan",
}).lazy(() =>
  import("./routes/store-scan").then((m) => createLazyRoute("/store/$storeId/scan")({ component: m.ScanPage })),
);

const storeTableRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "table/$qrToken",
}).lazy(() =>
  import("./routes/store-table").then((m) =>
    createLazyRoute("/store/$storeId/table/$qrToken")({ component: m.TableScanPage }),
  ),
);

const storeMenuRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "menu",
}).lazy(() =>
  import("./routes/store-menu").then((m) => createLazyRoute("/store/$storeId/menu")({ component: m.MenuPage })),
);

const storeCartRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "cart",
}).lazy(() =>
  import("./routes/store-cart").then((m) => createLazyRoute("/store/$storeId/cart")({ component: m.CartPage })),
);

const storeAddressRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "address",
}).lazy(() =>
  import("./routes/store-checkout").then((m) => createLazyRoute("/store/$storeId/address")({ component: m.AddressPage })),
);

const storePayRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "pay",
}).lazy(() =>
  import("./routes/store-checkout").then((m) => createLazyRoute("/store/$storeId/pay")({ component: m.PayPage })),
);

const storeStatusRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "status",
}).lazy(() =>
  import("./routes/store-status").then((m) =>
    createLazyRoute("/store/$storeId/status")({ component: m.OrderStatusPage }),
  ),
);

const storeLoyaltyRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "loyalty",
}).lazy(() =>
  import("./routes/store-loyalty").then((m) =>
    createLazyRoute("/store/$storeId/loyalty")({ component: m.LoyaltyPage }),
  ),
);

const storeRewardsRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "rewards",
}).lazy(() =>
  import("./routes/store-loyalty").then((m) =>
    createLazyRoute("/store/$storeId/rewards")({ component: m.RewardsPage }),
  ),
);

const storeOffersRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "offers",
}).lazy(() =>
  import("./routes/store-offers").then((m) =>
    createLazyRoute("/store/$storeId/offers")({ component: m.OffersPage }),
  ),
);

const storeVoucherDetailRoute = createRoute({
  getParentRoute: () => storeLayoutRoute,
  path: "vouchers/$voucherId",
}).lazy(() =>
  import("./routes/store-voucher-detail").then((m) =>
    createLazyRoute("/store/$storeId/vouchers/$voucherId")({ component: m.VoucherDetailPage }),
  ),
);

export const routeTree = rootRoute.addChildren([
  directoryRoute,
  receiptRoute,
  scanRoute,
  cardsRoute,
  meRoute,
  signInRoute,
  signUpRoute,
  protectRoute,
  receiptsRoute,
  offersRoute,
  storeLayoutRoute.addChildren([
    storeHomeRoute,
    storeScanRoute,
    storeTableRoute,
    storeMenuRoute,
    storeCartRoute,
    storeAddressRoute,
    storePayRoute,
    storeStatusRoute,
    storeLoyaltyRoute,
    storeRewardsRoute,
    storeOffersRoute,
    storeVoucherDetailRoute,
  ]),
]);
